"use client";

import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import Cookies from "js-cookie";
import { useEffect } from "react";
import { DEVICE_ID_COOKIE_NAME } from "../constants/cookies";
import { ampli } from "../generated/web";
import { useCookiebot } from "../providers/cookiebot-provider";

type AmpliProviderProps = React.PropsWithChildren<{
  apiKey: string | undefined;

  userId?: string;

  sessionReplaySampleRate?: number | string;
}>;

/**
 * Amplitude is our statistics and marketing library, and all tracking and goes through this.
 */
export function AmpliProvider({
  children,
  apiKey,

  userId,

  sessionReplaySampleRate,
}: AmpliProviderProps) {
  // Set up the cookie bot listener, so we can react to changes in consent
  const { consent, consentList } = useCookiebot();

  useEffect(() => {
    // Can't do anything ampli related without the api key
    if (!apiKey) return;

    // Get the deviceId from cookies using client side js
    const deviceId = Cookies.get(DEVICE_ID_COOKIE_NAME);

    // Initial load
    if (!ampli.isLoaded && consent?.statistics && (deviceId || userId)) {
      ampli.load({
        client: {
          apiKey,
          configuration: {
            autocapture: true,
            deviceId,
            userId,
          },
        },
      });

      // Add the current cookie
      if (consentList) {
        ampli.identify(
          userId,
          { Cookies: consentList },
          { device_id: deviceId },
        );
      }

      const sampleRate = parseSampleRate(sessionReplaySampleRate);
      if (sampleRate) ampli.client.add(sessionReplayPlugin({ sampleRate }));
      return;
    }

    // Opt in/out based on changed consent, and update corresponding user properties
    if (ampli.isLoaded) {
      ampli.client.setOptOut(!consent || consent.statistics === false);

      // Update the `Cookies` property to reflect consent
      if (consent?.statistics && consentList) {
        ampli.identify(
          userId,
          { Cookies: consentList },
          { device_id: deviceId },
        );
      }
    }
  }, [apiKey, userId, consent, consentList, sessionReplaySampleRate]);

  return <>{children}</>;
}

export function parseSampleRate(value: unknown) {
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (typeof value === "number") return value;

  return null;
}
